<template>
  <div class="flex items-center justify-between">
    <div class="flex items-center space-x-2">
      <div
        v-if="isRail"
        class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1D6BC0]/10 text-[#1D6BC0]"
        aria-hidden="true"
      >
        <TrainIcon class="h-6 w-6" />
      </div>
      <AirlineIcon
        v-else
        :size="AirlineIconSizeEnum.large"
        :airline="{
          iata: segment.displayAirline,
          title: segment.airlineTitle,
        }"
      />
      <div>
        <h5 class="text-text-neutral-primary text-base font-semibold">
          {{ titleText }}
        </h5>
        <p
          v-if="segment.durationTxt"
          class="text-text-neutral-quaternary text-sm"
        >
          {{
            t(isRail ? 'railSegmentDuration' : 'segmentDuration', {
              duration: segment.durationTxt,
            })
          }}
        </p>
      </div>
    </div>
    <div class="flex flex-col items-end space-y-1">
      <p class="text-text-neutral-quaternary text-sm">
        {{ numberText }}
      </p>
      <span
        v-if="segment.label"
        class="bg-background-negative-primary/10 text-text-negative-primary whitespace-nowrap rounded-[20px] px-2 py-1 text-xs font-medium"
      >
        {{ segment.label.title }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { ticketDetails } from 'shared';

  import { AirlineIcon, AirlineIconSizeEnum } from '../../../../shared/ui';
  import TrainIcon from '../../../../assets/images/train.svg?component';
  import type { MappedSegment } from '../../types';

  const props = defineProps<{ segment: MappedSegment }>();

  const { t } = useI18n();

  const isRail = computed(() => ticketDetails.lib.isRailSegment(props.segment));

  const titleText = computed(() => {
    if (!isRail.value) {
      return props.segment.airlineTitle;
    }
    const railName =
      props.segment.equipment?.name ??
      (ticketDetails.lib.isHighSpeedRail(props.segment)
        ? t('expressTrain')
        : t('train'));
    return props.segment.airlineTitle
      ? `${railName} · ${props.segment.airlineTitle}`
      : railName;
  });

  const numberText = computed(() => {
    if (isRail.value) {
      return t('trainNumber', { number: props.segment.flightNumber });
    }
    return t('flight', {
      airline: props.segment.airline,
      number: props.segment.flightNumber,
    });
  });
</script>

<i18n src="../../config/locales.json"></i18n>
